/**
 * The API takes a DateTime and floors it to the UTC day. Sending local midnight
 * from a timezone behind UTC would land on the previous day, so the picker's
 * "YYYY-MM-DD" is turned into UTC midnight explicitly rather than through
 * `new Date(value)`.
 */
export const toServiceDate = (isoDay: string): string =>
  new Date(`${isoDay}T00:00:00.000Z`).toISOString();

export const toIsoDay = (date: Date): string => date.toISOString().slice(0, 10);

export const todayIsoDay = (): string => toIsoDay(new Date());

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const isIsoDay = (value: string | null | undefined): value is string =>
  typeof value === "string" &&
  ISO_DAY.test(value) &&
  !Number.isNaN(new Date(`${value}T00:00:00.000Z`).getTime());

/**
 * The service day from the URL, or today. A native date input reports a
 * partially typed value as "", and "" is not null — so `params.get("date") ??
 * today` handed "" to `toServiceDate`, whose `toISOString` threw and took the
 * whole app to the error boundary. Anything that is not a complete, real day
 * reads as today.
 */
export const readIsoDay = (params: URLSearchParams): string => {
  const value = params.get("date");
  return isIsoDay(value) ? value : todayIsoDay();
};

export const formatServiceDay = (isoDay: string): string =>
  new Date(`${isoDay}T00:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

export const formatSentAt = (value: string): string =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

export const cptChipLabel = (
  cptCode: string | null | undefined,
  modifier: string | null | undefined,
): string | null => {
  if (!cptCode) return null;
  if (!modifier) return cptCode;
  return modifier.startsWith("-")
    ? `${cptCode}${modifier}`
    : `${cptCode}-${modifier}`;
};

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const formatEstimateCents = (cents: number): string =>
  USD.format(cents / 100);

export const MEDICARE_ESTIMATE_LABEL = "Estimate, based on Medicare";

export const NEEDS_PROGRAM_POSTAL_CODE_LABEL =
  "Set the program postal code to see estimates";
